import { Uuid, UuidString } from '~src/utils/UUID';
import { z } from 'zod';

const CommonMediaInfo = z.object({
  id: z
    .string()
    .uuid()
    .transform((u) => Uuid.from_string(u)),
  main_url: z.string(),
  ordering: z.number().gte(0),
});

const ImageInfo = CommonMediaInfo.extend({
  type: z.literal('image'),
  ultra_low_preview_url: z.string(),
});

const VideoInfo = CommonMediaInfo.extend({
  type: z.literal('video'),
  preview_image_url: z.string(),
});

const MediaInfo = z.discriminatedUnion('type', [ImageInfo, VideoInfo]);
export type MediaInfo = z.infer<typeof MediaInfo>;

export const ReactionType = z.union([
  z.literal('like'),
  z.literal('love'),
  z.literal('laugh'),
  z.literal('wow'),
  z.literal('sad'),
  z.literal('angry'),
]);
export type ReactionType = z.infer<typeof ReactionType>;

const CommentMention = z.object({
  comment_id: z
    .string()
    .uuid()
    .transform((u) => Uuid.from_string(u)),
  character_index: z.number().gte(0),
  mentioned_user_id: z
    .string()
    .uuid()
    .transform((u) => Uuid.from_string(u))
    .nullable(),
  inserted_at: z
    .string()
    .datetime({ offset: true })
    .transform((d) => new Date(d)),
});

const Comment = z.object({
  id: z
    .string()
    .uuid()
    .transform((u) => Uuid.from_string(u)),
  text_content: z.string().nullable(),
  group_id: z
    .string()
    .uuid()
    .transform((u) => Uuid.from_string(u)),
  post_id: z
    .string()
    .uuid()
    .transform((u) => Uuid.from_string(u)),
  commented_by_user_id: z
    .string()
    .uuid()
    .transform((u) => Uuid.from_string(u))
    .nullable(),
  mentions: CommentMention.array(),
  reactions: z.record(ReactionType, z.array(z.string().uuid()))
    .transform((record) => {
      const map = new Map<ReactionType, Set<UuidString>>();
      for (const [key, val] of Object.entries(record)) {
        // @ts-ignore
        map.set(key as ReactionType, new Set(val));
      }
      return map;
    }),
  inserted_at: z
    .string()
    .datetime({ offset: true })
    .transform((d) => new Date(d)),
});
export type Comment = z.infer<typeof Comment>;

export const Post = z.object({
  id: z
    .string()
    .uuid()
    .transform((u) => Uuid.from_string(u)),
  text_content: z.string(),
  media: MediaInfo.array(),
  groups: z
    .string()
    .uuid()
    .transform((u) => Uuid.from_string(u))
    .array(),
  posted_by: z
    .string()
    .uuid()
    .transform((u) => Uuid.from_string(u)),
  mentioned_users: z
    .string()
    .uuid()
    .transform((u) => Uuid.from_string(u))
    .array(),
  reactions: z.record(ReactionType, z.array(z.string().uuid()))
    .transform((record) => {
      const map = new Map<ReactionType, Set<UuidString>>();
      for (const [key, val] of Object.entries(record)) {
        // @ts-ignore
        map.set(key as ReactionType, new Set(val));
      }
      return map;
    }),
  comments: z
    .object({})
    .catchall(Comment.array())
    .transform((o) => {
      let asMap = new Map<UuidString, Comment[]>();

      for (const [groupId, commentArray] of Object.entries(o)) {
        asMap.set(Uuid.from_string(groupId), commentArray);
      }

      return asMap;
    })
    .nullable(),
  posted_at: z
    .string()
    .datetime({ offset: true })
    .transform((d) => new Date(d)),
});
export type Post = z.infer<typeof Post>;

export const PostArray = z.array(Post);
export type PostArray = z.infer<typeof PostArray>;

export const CommentArray = z.array(Comment);
export type CommentArray = z.infer<typeof CommentArray>;
